import React from "react";
import ButtonBase from "@material-ui/core/ButtonBase";
import { ReactComponent as GoonoLogoIcon } from "@assets/images/logo.svg";
import ExitToAppRoundedIcon from "@material-ui/icons/ExitToAppRounded";
import { ReactComponent as PersonIcon } from "@assets/images/person.svg";
import ScreenURL from "src/routes/route_list";
import { useServiceHeaderStyle } from "../styles";
import KeyboardArrowDownRoundedIcon from "@material-ui/icons/KeyboardArrowDownRounded";
import Popover from "@material-ui/core/Popover";
import HistoryIcon from "@material-ui/icons/HistoryRounded";
import AddIcon from "@material-ui/icons/Add";
import OpenInNewRoundedIcon from "@material-ui/icons/OpenInNewRounded";
import Flex from "@ui/goono/components/layouts/Flex";
import GoonoTypography from "@ui/goono/components/typo/GoonoTypography";
import { Outlet, useNavigate } from "react-router-dom";
import { useLoginUserInfo } from "@utils/hooks/service/useLoginUserInfo";
import HeaderMenuOption from "./HeaderMenuOption";
import TextButton from "@ui/goono/components/buttons/TextButton";
import SolidButton from "@ui/goono/components/buttons/SolidButton";
import { useHandleLogout } from "src/handler/token/useHnadleLogout";
import clsx from "clsx";

const SALES_REPORT_LINK =
  "https://script.google.com/macros/s/AKfycbzLugAKTc-7W5Oan6JqvpeUAf5YDZaAhmU-59rELSxxALo0VosP9u0ltzM6zFd7PHo8/exec";

export default function ServiceHeader() {
  const classes = useServiceHeaderStyle();
  const userInfo = useLoginUserInfo();
  const navigate = useNavigate();
  const { logout } = useHandleLogout();

  const replaceURI = (URI: string) => {
    navigate(URI, { replace: true });
  };

  const renderHeaderLogo = () => {
    return (
      <ButtonBase
        disableRipple
        onClick={() => replaceURI(ScreenURL.MANAGE_CLIENT)}
        className={classes.logoButton}
      >
        <GoonoLogoIcon />
      </ButtonBase>
    );
  };

  const renderButtonMenu = (params: {
    title: string;
    optionTitle: { first: string; second: string };
    firstPageURL: string;
    secondPageURL: string;
  }) => {
    return (
      <div className={classes.menuOption}>
        <SolidButton
          buttonType="white"
          endIcon={
            <KeyboardArrowDownRoundedIcon
              style={{ fontSize: 18, opacity: 0.7 }}
            />
          }
          onClick={() => replaceURI(params.firstPageURL)}
          className={clsx(
            classes.menuItem,
            (window.location.pathname === params.firstPageURL ||
              window.location.pathname === params.secondPageURL) &&
              "activation"
          )}
        >
          {params.title}
        </SolidButton>
        <HeaderMenuOption
          navigateFirstPage={() => replaceURI(params.firstPageURL)}
          navigateSecondPage={() => replaceURI(params.secondPageURL)}
          firstOptionTitle={params.optionTitle.first}
          secondOptionTitle={params.optionTitle.second}
          className={classes.menuOptionContent}
        />
      </div>
    );
  };

  const renderHeaderMenuList = () => {
    return (
      <Flex container style={{ marginLeft: 20 }} alignItems="center">
        {renderButtonMenu({
          title: "사용자 관리",
          firstPageURL: ScreenURL.MANAGE_CLIENT,
          secondPageURL: ScreenURL.MANAGE_WORKSPACE,
          optionTitle: {
            first: "전체 사용자 보기",
            second: "전체 워크스페이스 보기",
          },
        })}
        {renderButtonMenu({
          title: "조직 관리",
          firstPageURL: ScreenURL.MANAGE_ORGANIZATION,
          secondPageURL: ScreenURL.MANAGE_ORGANIZATION_ADMIN,
          optionTitle: {
            first: "전체 조직 보기",
            second: "전체 조직 관리자 보기",
          },
        })}
        <TextButton
          className={clsx(
            classes.menuItem,
            window.location.pathname === ScreenURL.MANAGE_INSTITUTION &&
              "activation"
          )}
          onClick={() => replaceURI(ScreenURL.MANAGE_INSTITUTION)}
        >
          고객사 관리
        </TextButton>
        <TextButton
          className={clsx(
            classes.menuItem,
            window.location.pathname === ScreenURL.NOTICE && "activation"
          )}
          onClick={() => replaceURI(ScreenURL.NOTICE)}
        >
          공지사항
        </TextButton>
        <TextButton
          className={clsx(
            classes.menuItem,
            window.location.pathname === ScreenURL.MANAGE_OAUTH_CLIENT &&
              "activation"
          )}
          onClick={() => replaceURI(ScreenURL.MANAGE_OAUTH_CLIENT)}
        >
          OAuth 관리
        </TextButton>
        <TextButton
          className={clsx(
            classes.menuItem,
            window.location.pathname === ScreenURL.DASHBOARD && "activation"
          )}
          onClick={() => window.open(SALES_REPORT_LINK)}
          endIcon={<OpenInNewRoundedIcon style={{ fontSize: 16 }} />}
        >
          세일즈 툴
        </TextButton>
      </Flex>
    );
  };

  const renderHeaderCreditButton = () => {
    return (
      <SolidButton
        buttonSize="lg"
        startIcon={<AddIcon />}
        onClick={() => replaceURI(ScreenURL.CREATE_PAYMENT_WINDOW)}
      >
        결제창 생성
      </SolidButton>
    );
  };

  const renderPaymentHistoryButton = () => {
    return (
      <SolidButton
        startIcon={<HistoryIcon />}
        onClick={() => replaceURI(ScreenURL.PAYMENT_WINDOW_CREATION_HISTORY)}
      >
        결제창 생성 내역
      </SolidButton>
    );
  };

  const [accountAnchorEl, setAccountAnchorEl] =
    React.useState<HTMLElement | null>(null);
  const isAccountMenuOpen = Boolean(accountAnchorEl);
  const closeAccountMenu = () => setAccountAnchorEl(null);

  const renderHeaderAccount = () => {
    return (
      <>
        <ButtonBase
          disableRipple
          className={classes.user_email}
          onClick={(e) => setAccountAnchorEl(e.currentTarget)}
          aria-label="계정 메뉴"
        >
          <PersonIcon />
          <GoonoTypography type="ele1_m">{userInfo.email}</GoonoTypography>
          <KeyboardArrowDownRoundedIcon
            className={clsx(
              classes.account_arrow,
              isAccountMenuOpen && classes.account_arrow_open
            )}
          />
        </ButtonBase>
        <Popover
          open={isAccountMenuOpen}
          anchorEl={accountAnchorEl}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          onClose={closeAccountMenu}
          className={classes.account_popover}
        >
          <ButtonBase
            className={classes.account_popover_item}
            onClick={() => {
              closeAccountMenu();
              void logout();
            }}
          >
            <ExitToAppRoundedIcon />
            <GoonoTypography type="b3_r">로그아웃</GoonoTypography>
          </ButtonBase>
        </Popover>
      </>
    );
  };

  const renderHeaderLeftItems = () => {
    return (
      <Flex
        container
        item
        flexDirection="row"
        alignItems="center"
        className={classes.headerLeft}
      >
        {renderHeaderLogo()}
        {renderHeaderMenuList()}
      </Flex>
    );
  };

  const renderHeaderRightItems = () => {
    return (
      <Flex
        container
        item
        flexDirection="row"
        alignItems="center"
        className={classes.headerRight}
      >
        <Flex container item alignItems={`center`} gap="10px">
          {renderHeaderCreditButton()}
          {renderPaymentHistoryButton()}
          {renderHeaderAccount()}
        </Flex>
      </Flex>
    );
  };

  return (
    <>
      <div className={classes.header}>
        <div className={classes.header_inner}>
          {renderHeaderLeftItems()}
          {renderHeaderRightItems()}
        </div>
      </div>
      <Outlet />
    </>
  );
}
